import { ajaxGet } from 'lib/ajax-helpers';
import { sendTrackingEvent } from 'components/analytics/conversions/tracking';
import { vatErrors } from 'lib/tax-constants';
import { parseErrorMsg } from 'lib/validation-helpers';
import {
  EC_BEGIN_CHECKOUT,
  EC_PURCHASE_COMPLETE,
} from 'components/analytics/conversions/constants/actions';
import { buildPlanItems, transactionAmount } from 'lib/plan-helpers';
import FormController from '../../../controllers/form-controller';

type OrderData = {
  'discount_amount':string
  plan:Record<string, unknown>
  'base_price':string
  tax:string | number
  total:string
}

type CheckoutError = {
  'type':string
  'message':string
  'code':string
}

type RefundCreditsAjaxParams = {
  'plan_id':string
  'refund_credits'?:boolean | string
}

type ModalContext = 'triggered' | 'click'

export default class PurchaseModalController extends FormController {
  declare readonly element: HTMLElement;

  declare readonly flagLoaded: () => void;

  declare formValid:boolean;

  declare readonly orderSubtotalTarget:HTMLElement;

  declare readonly orderTaxTarget:HTMLElement;

  declare readonly orderTotalTarget:HTMLElement;

  declare readonly orderSummaryLoadingTarget:HTMLElement;

  declare readonly planSelectTarget:HTMLInputElement;

  declare readonly formSubmitBtnTarget:HTMLElement;

  declare readonly errorContainerTarget:HTMLElement;

  declare readonly refundCreditsFieldTarget:HTMLInputElement;

  declare readonly hasRefundCreditsFieldTarget:boolean;

  declare readonly contextValue:ModalContext;

  declare readonly orderApiUrlValue:string;

  declare readonly redirectUrlValue:string;

  declare selectedPlanValue:string;

  declare readonly allPlansDataValue:string;

  declare readonly checkoutUrlValue:string;

  static targets = [
    'orderSubtotal',
    'orderTax',
    'orderTotal',
    'orderSummaryLoading',
    'planSelect',
    'formSubmitBtn',
    'errorContainer',
    'refundCreditsField',
  ];

  static values = {
    context: String,
    orderApiUrl: String,
    redirectUrl: String,
    selectedPlan: String,
    allPlansData: String,
    checkoutUrl: String,
  }

  connect() {
    const selectedPlan = JSON.parse(this.selectedPlanValue);

    sendTrackingEvent({
      data: {
        action: EC_BEGIN_CHECKOUT,
        items: buildPlanItems({ plans: selectedPlan }),
        non_interaction: true,
      },
    });

    this.flagLoaded();
  }

  updatePlan = (event:Event) => {
    const target = event.currentTarget as HTMLInputElement;

    this.orderSummaryLoadingTarget.classList.remove('is-hidden');
    this.formSubmitBtnTarget.classList.add('is-disabled');

    const params:RefundCreditsAjaxParams = {
      plan_id: target.value,
    };

    if (this.hasRefundCreditsFieldTarget) {
      params.refund_credits = this.refundCreditsFieldTarget.value;
    }

    ajaxGet(this.orderApiUrlValue,
      (response) => {
        this.updateTotals(response.data);
        this.orderSummaryLoadingTarget.classList.add('is-hidden');
        this.formSubmitBtnTarget.classList.remove('is-disabled');
      },
      (error) => {
        console.error(error); // eslint-disable-line no-console
      },
      {
        params,
      });

    this.updateSelectedPlan(target.value);
  }

  updateSelectedPlan(planId:string) {
    const plansData = JSON.parse(this.allPlansDataValue);
    const selectedPlan = plansData.find((x) => x.remote_id === planId);

    selectedPlan.plan_type = 'subscription';

    this.selectedPlanValue = JSON.stringify(selectedPlan);
  }

  updateTotals(orderData:OrderData) {
    this.orderSubtotalTarget.innerHTML = orderData.base_price;
    this.orderTaxTarget.innerHTML = orderData.tax.toString();
    this.orderTotalTarget.innerHTML = orderData.total;
  }

  processErrors = (event) => {
    const [data] = event.detail;
    const { errors } = data;

    if (errors) {
      const errorsArr = [];
      const errorLabelsArr = [];

      let errorMessageKey;

      errors.forEach((error:CheckoutError) => {
        const errorMessage = error.message;

        if (errorMessage.indexOf('PaymentDeclinedCVVError') > -1) {
          errorMessageKey = 'invalid_cvv';
        } else if (errorMessage.indexOf('Gateway Rejected: cvv') > -1) {
          errorMessageKey = 'invalid_cvv';
        } else if (errorMessage.indexOf('PaymentDeclinedError') > -1) {
          errorMessageKey = 'payment_declined';
        } else if (errorMessage.indexOf('Plan not found') > -1) {
          errorMessageKey = 'plan_not_found';
        } else if (vatErrors.includes(errorMessage)) {
          // Vat validation errors
          errorMessageKey = 'invalid';
        } else {
          // Unhandled errors display on card number input
          errorMessageKey = 'payment_failed';
        }

        const errorString = parseErrorMsg(this.element, errorMessageKey);
        errorsArr.push(`<span class="purchase-modal__error-message">${errorString}</span>`);
        errorLabelsArr.push(errorString);
      });

      this.errorContainerTarget.innerHTML = errorsArr.join('');
      this.errorContainerTarget.classList.remove('is-hidden');

      this.sendErrorEvents({ errors: errorLabelsArr.join(', ') });
    }
  }

  submitPurchase = (event) => {
    super.validateFields();

    if (!this.formValid) event.preventDefault();
  }

  // This can be overridden in child controllers to send
  // post-purchase success callbacks, etc.
  purchaseSuccess(event) {
    this.processPurchase({ event });
  }

  processPurchase({ event, callback = null }) {
    const [data] = event.detail;

    this.sendECEvents({ purchaseData: data });
    this.sendPurchaseEvents();

    if (callback) callback(data);

    window.location.href = this.redirectUrlValue;
  }

  editInCheckout = (event) => {
    event.preventDefault();
    if (this.hasRefundCreditsFieldTarget) {
      window.location.href = `${this.checkoutUrlValue}/${this.planSelectTarget.value}?credit_refund_upgrade=true`;
    } else {
      window.location.href = `${this.checkoutUrlValue}/${this.planSelectTarget.value}`;
    }
  }

  sendECEvents = ({ purchaseData }) => {
    const selectedPlan = JSON.parse(this.selectedPlanValue);

    sendTrackingEvent({
      element: this.element,
      data: {
        action: EC_PURCHASE_COMPLETE,
        transaction_id: purchaseData.transaction_id,
        value: transactionAmount({ plan: selectedPlan }),
        currency: selectedPlan.currency,
        tax: purchaseData.tax,
        items: buildPlanItems({ plans: selectedPlan }),
      },
    });
  }

  sendPurchaseEvents() { /* do nothing */ }

  sendErrorEvents({ errors }) {
    console.error(errors); // eslint-disable-line no-console
  }

  lockForm() { /* to be overriden in free-trial-upgrade-modal controller */ }

  unlockForm() { /* to be overriden in free-trial-upgrade-modal controller */ }
}
